import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styles from './PortfolioHealth.module.sass';
import type { HoldingData } from 'pages/Dashboard/Portfolio';
import PortfolioCharting from 'components/PortfolioCharting';
import type { RootState } from '../../redux/store';
import Spinner from 'components/Spinner';

export type MetricType = 'portfolio' | 'market' | 'drawdown' | null;

interface PortfolioHealthProps {
  selectedMetric: MetricType;
  onMetricSelect: (metric: MetricType) => void;
  activeView: 'metrics' | 'holdings';
  onViewChange: (view: 'metrics' | 'holdings') => void;
  holdings: HoldingData[];
}

interface ChartDataPoint {
  name: string;
  value: number;
}

const getColorStyles = (value: number) => {
  if (value < 2) {
    return {
      progress: {
        background: '#f44336',
        boxShadow: '0 0 10px rgba(244, 67, 54, 0.4)'
      },
      card: {
        borderColor: 'rgba(244, 67, 54, 0.3)',
        boxShadow: '0 0 15px rgba(244, 67, 54, 0.2)'
      },
      glow: {
        textShadow: '0 0 10px rgba(244, 67, 54, 0.8)'
      }
    };
  }
  if (value < 3.5) {
    return {
      progress: {
        background: '#9c27b0',
        boxShadow: '0 0 10px rgba(156, 39, 176, 0.4)'
      },
      card: {
        borderColor: 'rgba(156, 39, 176, 0.3)',
        boxShadow: '0 0 15px rgba(156, 39, 176, 0.2)'
      },
      glow: {
        textShadow: '0 0 10px rgba(156, 39, 176, 0.8)'
      }
    };
  }
  return {
    progress: {
      background: '#73F6FF',
      boxShadow: '0 0 10px rgba(115, 246, 255, 0.3)'
    },
    card: {
      borderColor: 'rgba(115, 246, 255, 0.2)',
      boxShadow: '0 0 15px rgba(115, 246, 255, 0.1)'
    },
    glow: {
      textShadow: '0 0 10px rgba(115, 246, 255, 0.8)'
    }
  };
};

const getRiskLevel = (score: number | null, isLoading: boolean): React.ReactNode => {
  // Reduce spinner size
  if (isLoading) return <Spinner sx={{ width: '3em', height: '3em' }} />;
  if (score === null) return "Unknown";
  if (score < 2) return "High Risk";
  if (score < 3.5) return "Moderate Risk";
  return "Low Risk";
};

const PortfolioHealth: React.FC<PortfolioHealthProps> = ({
  selectedMetric,
  onMetricSelect,
  activeView,
  onViewChange,
  holdings
}) => {
  const dispatch = useDispatch();
  const selectedToken = useSelector((state: RootState) => state.user.selectedToken);
  
  // Updated selectors to use new Redux state fields
  const portfolioScore = useSelector((state: RootState) => state.portfolio.portfolioScore);
  // const diversityScore = useSelector((state: RootState) => state.portfolio.diversityScore);
  const marketScore = useSelector((state: RootState) => state.portfolio.marketScore);

  const isPortBenchLoading = useSelector((state: RootState) => state.portfolio.loading.portBench);
  // const isCorrelationsLoading = useSelector((state: RootState) => state.portfolio.loading.assetCorrelations);
  const isMaxDrawDownLoading = useSelector((state: RootState) => state.portfolio.loading.maxDrawDown);

  const formatScore = (score: number | null): string => {
    if (score === null) return "";
    return `${score.toFixed(1)} / 5.0`;
  };

  const renderMetric = (
    label: string, 
    value: string, 
    score: number | null, 
    metricType: MetricType
  ) => {
    const colorStyles = getColorStyles(score ?? 3);
    const isSelected = selectedMetric === metricType;
    const scoreDisplay = formatScore(score);
    
    return (
      <div 
        className={`${styles.metric} ${isSelected ? styles.selected : ''}`}
        onClick={() => onMetricSelect(metricType)}
        style={{
          ...colorStyles.card,
          ...(isSelected && {
            boxShadow: `0 0 20px ${colorStyles.progress.background}`,
            borderColor: colorStyles.progress.background,
            transition: 'all 0.3s ease'
          })
        }}
      >
        <div className={styles.metricContent}>
          <div className={styles.metricStats}>
            <div className={styles.metricLabel}>{label}</div>
            <div className={styles.scoreContent}>
              <div className={styles.scoreSegments}>
                {[...Array(5)].map((_, i) => {
                  const scoreValue = score ?? 3;
                  const isFullyFilled = i < Math.floor(scoreValue);
                  const isPartiallyFilled = i === Math.floor(scoreValue);
                  const partialFillPercentage = isPartiallyFilled ? (scoreValue % 1) * 100 : 0;
                  
                  let segmentStyle = {};
                  if (isFullyFilled) {
                    segmentStyle = colorStyles.progress;
                  } else if (isPartiallyFilled && partialFillPercentage > 0) {
                    const color = colorStyles.progress.background;
                    segmentStyle = {
                      background: `linear-gradient(to right, ${color} ${partialFillPercentage}%, rgba(115, 246, 255, 0.1) ${partialFillPercentage}%)`,
                      boxShadow: colorStyles.progress.boxShadow
                    };
                  }
                  
                  return (
                    <div 
                      key={i}
                      className={`${styles.segment} ${isFullyFilled ? styles.segmentFilled : ''}`}
                      style={segmentStyle}
                    />
                  );
                })}
              </div>
              <div 
                className={styles.metricValue}
                style={(score ?? 3) < 3.5 ? colorStyles.glow : undefined}
              >
                <span>{value}</span>
                {scoreDisplay && (
                  <span className={styles.numericalScore}>{scoreDisplay}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getMetricTitle = (metric: MetricType): string => {
    switch (metric) {
      case 'market':
        return 'Market Comparison';
      case 'drawdown':
        return 'Drawdown Analysis';
      default:
        return 'Market Comparison'; // Changed default to Market Comparison
    }
  };

  return (
    <div className={styles.healthContainer}>
      <div className={styles.metricsSection}>
        <div className={styles.metricRow}>
          {/* Portfolio Diversity metric removed */}
          {renderMetric(
            "Market Comparison",
            getRiskLevel(marketScore, isPortBenchLoading),
            marketScore,
            'market'
          )}
          {renderMetric(
            "Drawdown",
            getRiskLevel(portfolioScore, isMaxDrawDownLoading),
            portfolioScore,
            'drawdown'
          )}
        </div>
      </div>
      
      <div className={styles.chartSection}>
        <div className={styles.tabContainer}>
          <div className={styles.tabContent}>
            <button
              onClick={() => onViewChange('metrics')}
              className={`${styles.tabButton} ${activeView === 'metrics' ? styles.active : ''}`}
            >
              {getMetricTitle(selectedMetric)}
            </button>
            <button
              onClick={() => onViewChange('holdings')}
              className={`${styles.tabButton} ${activeView === 'holdings' ? styles.active : ''}`}
            >
              {selectedToken ? `${selectedToken} Chart` : 'Asset Chart'}
            </button>
          </div>
        </div>
  
        <PortfolioCharting 
          selectedMetric={selectedMetric} 
          activeView={activeView}
          holdings={holdings}
        />
      </div>
    </div>
  );
};

export default PortfolioHealth;